/**
 * @file NonCashWorkspace.jsx
 * @module ui/src/components/admin/NonCashWorkspace.jsx
 * @description
 *   C-69 — Non-Cash Awards Workspace. Runtime instrument surface.
 *   EXECUTE → Programs & Adjustments → Non-Cash Awards.
 *
 *   3 tabs: Award Types · Grants · Activity
 *
 *   S926 — New component. Replaces <ComingSoonSection label="Non-Cash Awards" />
 *   in ProgramsAndAdjustmentsHub.jsx. Backend: p4_non_cash_router.py (all 7
 *   routes live, AD-S924-02). Do NOT build a new non-cash backend router.
 *
 *   Architecture:
 *   - Standalone runtime instrument — does NOT use usePlanDocument,
 *     usePlanGovernance, or usePlanValidation (design-time hooks).
 *   - No custom hook needed — direct fetch pattern with AbortController +
 *     mounted ref (same as ContestWorkspace.jsx pattern — OD-S924-B).
 *   - Standard tier — NO enterprise feature gate (p4_non_cash_router.py
 *     never calls tenant_has_plan_type).
 *   - No program-level lifecycle (no pool_status equivalent). Grant IS the
 *     output — grant lifecycle: pending → approved → issued.
 *   - No separate Approval tab. Approval is per-grant inline action in
 *     the Grants tab (arch spec EP68 note: "Grant creation is the approval
 *     mechanism — no separate approval endpoint required").
 *   - Platform-seeded award types (tenant_id = sentinel
 *     00000000-0000-0000-0000-000000000000) appear in §67.3 list. The
 *     router does NOT return tenant_id in the list payload, so platform
 *     rows cannot be detected client-side. Per OD-S926 decision: platform
 *     distinction not surfaced in V1. PATCH will be rejected server-side
 *     for platform rows (tenant_id mismatch). UI shows a note on edit
 *     failure guiding users to create a new type instead.
 *   - NN-10: granted_by ≠ approving user on §67.5 approve ONLY.
 *     NOT enforced on §67.4 grant creation.
 *   - NN-36: tenant_super_admin blocked on every route (server-enforced).
 *   - Dual-writer: contest + bonus_pool grant_sources are read-only in UI.
 *     Only manager_spot and spiff sources accept approve/issue actions.
 *   - Activity tab: entity_type='non_cash_award_grant', entity_id=grant_id
 *     (uuid PK). Requires a grant to be selected. Falls back to unfiltered
 *     non_cash_award_grant activity when no grant selected.
 *   - Inline panels per OD-S924-B. Refactor to separate files when any
 *     panel exceeds ~150L.
 *
 *   Routes wired (p4_non_cash_router.py):
 *   §67.1  POST   /non-cash-award-types              — create award type
 *   §67.2  PATCH  /non-cash-award-types/{id}         — patch award type
 *   §67.3  GET    /non-cash-award-types              — list award types
 *   §67.4  POST   /non-cash-award-grants             — create grant
 *   §67.5  POST   /non-cash-award-grants/{id}/approve — approve grant
 *   §67.6  POST   /non-cash-award-grants/{id}/issue   — issue grant
 *   §67.7  GET    /non-cash-award-grants             — list grants
 *
 *   Activity:
 *   §30.1  GET    /audit-logs  entity_type=non_cash_award_grant entity_id=<uuid>
 *
 *   CSS: index.css + tokens.css. All colours via rgb(var(--color-*)).
 *   Button classes: .primary-button .secondary-button .icon-button .text-button
 *   Table classes: .modifier-manager-table-wrapper .modifier-manager-table
 *   Badge classes: .modifier-manager-badge--*
 *
 *   UI directives (carry from S924/S925):
 *   D-1: Tables → scrollable + sortable + searchable + clickable drill-down
 *   D-2: Long screens → collapsible sections (secondary collapsed by default)
 *
 * @author data-fit LLC · DecisionFlow · S926
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = '/api/v1';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'award_types', label: 'Award Types' },
  { key: 'grants',      label: 'Grants' },
  { key: 'activity',   label: 'Activity' },
];

// §67.3 GET response keys (confirmed from router lines 371–373)
const AWARD_CATEGORIES = ['points', 'merchandise', 'travel', 'recognition', 'gift_card'];

const AWARD_CATEGORY_LABELS = {
  points:       'Points',
  merchandise:  'Merchandise',
  travel:       'Travel',
  recognition:  'Recognition',
  gift_card:    'Gift Card',
};

// Grant sources (confirmed from router _ROUTER_GRANT_SOURCES / _ENGINE_GRANT_SOURCES)
const ROUTER_GRANT_SOURCES = new Set(['manager_spot', 'spiff']);
const ENGINE_GRANT_SOURCES = new Set(['contest', 'bonus_pool']);

const GRANT_SOURCE_LABELS = {
  manager_spot: 'Manager Spot',
  spiff:        'SPIFF',
  contest:      'Contest (engine)',
  bonus_pool:   'Bonus Pool (engine)',
};

// Grant status lifecycle: pending → approved → issued (redeemed in DDL but no router endpoint)
const GRANT_STATUS_BADGE = {
  pending:  'modifier-manager-badge--warning',
  approved: 'modifier-manager-badge--complete',
  issued:   'modifier-manager-badge--readonly',
  redeemed: 'modifier-manager-badge--readonly',
};

const GRANT_STATUS_LABEL = {
  pending:  'Pending',
  approved: 'Approved',
  issued:   'Issued',
  redeemed: 'Redeemed',
};

// ---------------------------------------------------------------------------
// Shared helpers (follow ContestWorkspace.jsx pattern exactly)
// ---------------------------------------------------------------------------

function _iso(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function _fmt(amount) {
  if (amount == null) return '—';
  const num = parseFloat(amount);
  if (isNaN(num)) return String(amount);
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

async function apiFetch(url, opts = {}) {
  const r = await fetch(url, { credentials: 'include', ...opts });
  if (!r.ok) {
    const b = await r.json().catch(() => ({}));
    const err = new Error(b?.error?.message || b?.detail || `Request failed (${r.status})`);
    err.status = r.status;
    err.errorCode = b?.error?.code ?? null;
    throw err;
  }
  return r.json();
}

// ---------------------------------------------------------------------------
// S928 — OI-S920-403-APPROVAL-ERROR-NOT-SURFACED
// Shared SoD 403 error classifier for all approval actions in this workspace.
// Returns an actionable message string; caller sets its own error state.
// ---------------------------------------------------------------------------
function handleApprovalError(err) {
  const is403 = err.status === 403;
  const isSoD = is403
    || (err.errorCode && err.errorCode.toLowerCase().includes('segregation'))
    || (err.message  && (
         err.message.toLowerCase().includes('segregation') ||
         err.message.toLowerCase().includes('cannot approve') ||
         err.message.toLowerCase().includes('created this') ||
         err.message.toLowerCase().includes('sod') ||
         err.message.toLowerCase().includes('separation of duties')
       ));
  if (isSoD) {
    return 'You cannot approve this — Separation of Duties (NN-10) prevents the creator from approving their own record. A different comp admin must perform this action.';
  }
  return err.message || 'An unexpected error occurred.';
}

// ---------------------------------------------------------------------------
// Shared primitives (follow ContestWorkspace.jsx pattern exactly)
// ---------------------------------------------------------------------------

function Field({ label, hint, error, children, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>
          {label}{required && <span style={{ color: 'rgb(var(--color-danger))' }}> *</span>}
        </label>
      )}
      {children}
      {hint && !error && <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', marginTop: 2 }}>{hint}</span>}
      {error && <span style={{ fontSize: 12, color: 'rgb(var(--color-danger))', marginTop: 2 }} role="alert">{error}</span>}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid rgb(var(--color-border))' }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'rgb(var(--color-text-muted))', minWidth: 200 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'rgb(var(--color-text))' }}>{value ?? '—'}</span>
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen = true, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: open ? '1px solid rgb(var(--color-border))' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'rgb(var(--color-text))' }}>{title}</span>
          {badge && <span className={`modifier-manager-badge ${badge.cls}`}>{badge.label}</span>}
        </div>
        <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

// Style constants (same as ContestWorkspace S object)
const S = {
  input: {
    border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '6px 10px',
    fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))',
    width: '100%', boxSizing: 'border-box',
  },
  select: {
    border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '6px 10px',
    fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))',
    width: '100%', boxSizing: 'border-box',
  },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
};

// ===========================================================================
// TAB 1 — AWARD TYPES
// §67.3 GET /non-cash-award-types (tenant + platform sentinel rows)
// §67.1 POST /non-cash-award-types (create tenant-scoped type)
// §67.2 PATCH /non-cash-award-types/{id} (patch: name, point_value,
//         estimated_cash_value, is_active only — code + category immutable)
// ===========================================================================
function AwardTypesPanel() {
  const [types, setTypes]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch]       = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterActive, setFilterActive]     = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    award_type_code: '', award_type_name: '', award_category: 'recognition',
    point_value: '', estimated_cash_value: '', currency_code: '', is_active: true,
  });
  const [creating, setCreating]   = useState(false);
  const [createError, setCreateError] = useState(null);

  // Edit form — keyed by award_type_id
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm]   = useState({
    award_type_name: '', point_value: '', estimated_cash_value: '', is_active: true,
  });
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadTypes = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filterCategory) params.set('award_category', filterCategory);
      if (filterActive !== '') params.set('is_active', filterActive);
      const d = await apiFetch(`${API_BASE}/non-cash-award-types?${params}`);
      if (mountedRef.current) setTypes(d.items ?? []);
    } catch (e) {
      if (mountedRef.current) setError(e.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [filterCategory, filterActive, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadTypes(); }, [loadTypes]);

  const filtered = types.filter(t =>
    !search ||
    t.award_type_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.award_type_code?.toLowerCase().includes(search.toLowerCase())
  );

  function resetCreateForm() {
    setCreateForm({ award_type_code: '', award_type_name: '', award_category: 'recognition',
      point_value: '', estimated_cash_value: '', currency_code: '', is_active: true });
    setCreateError(null);
  }

  async function handleCreate() {
    setCreating(true); setCreateError(null);
    try {
      const body = {
        award_type_code:      createForm.award_type_code,
        award_type_name:      createForm.award_type_name,
        award_category:       createForm.award_category,
        point_value:          createForm.point_value || null,
        estimated_cash_value: createForm.estimated_cash_value || null,
        currency_code:        createForm.currency_code || null,
        is_active:            createForm.is_active,
      };
      await apiFetch(`${API_BASE}/non-cash-award-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      resetCreateForm();
      setShowCreate(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  }

  function startEdit(t) {
    setEditingId(t.award_type_id);
    setEditForm({
      award_type_name:      t.award_type_name ?? '',
      point_value:          t.point_value ?? '',
      estimated_cash_value: t.estimated_cash_value ?? '',
      is_active:            t.is_active ?? true,
    });
    setSaveError(null);
  }

  async function handleSaveEdit(id) {
    setSaving(true); setSaveError(null);
    try {
      const body = {};
      if (editForm.award_type_name) body.award_type_name = editForm.award_type_name;
      if (editForm.point_value !== '') body.point_value = editForm.point_value;
      if (editForm.estimated_cash_value !== '') body.estimated_cash_value = editForm.estimated_cash_value;
      body.is_active = editForm.is_active;
      await apiFetch(`${API_BASE}/non-cash-award-types/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setEditingId(null);
      setRefreshKey(k => k + 1);
    } catch (e) {
      // Platform rows will fail with tenant mismatch — surface helpful note
      setSaveError(e.message + (e.message.includes('404') || e.message.includes('not found')
        ? ' — This may be a platform-seeded type. Create a new type in your tenant instead.'
        : ''));
    } finally {
      setSaving(false);
    }
  }

  const createValid = createForm.award_type_code && createForm.award_type_name && createForm.award_category;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <input type="text" placeholder="Search types…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }} />
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}>
          <option value="">All categories</option>
          {AWARD_CATEGORIES.map(c => <option key={c} value={c}>{AWARD_CATEGORY_LABELS[c]}</option>)}
        </select>
        <select value={filterActive} onChange={e => setFilterActive(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}>
          <option value="">Active &amp; inactive</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
        <button type="button" onClick={() => setRefreshKey(k => k + 1)}
          style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', background: 'none', border: 'none', cursor: 'pointer' }}>
          ↻ Refresh
        </button>
        <button type="button" className="primary-button" style={{ marginLeft: 'auto' }}
          onClick={() => { resetCreateForm(); setShowCreate(true); }}>
          + New Award Type
        </button>
      </div>

      {/* Row count */}
      <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
        {loading ? 'Loading…' : `${filtered.length} of ${types.length} type${types.length !== 1 ? 's' : ''}`}
      </div>

      {error && <div style={{ padding: '12px 16px', color: 'rgb(var(--color-danger))', fontSize: 13 }} role="alert">{error}</div>}

      {/* Award types table — D-1 */}
      {!loading && filtered.length === 0 && !showCreate && (
        <div className="modifier-manager-empty-state">
          <h4>No award types</h4>
          <p>Create your first award type or review platform-provided templates above.</p>
          <button type="button" className="primary-button" onClick={() => { resetCreateForm(); setShowCreate(true); }}>
            + New Award Type
          </button>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="modifier-manager-table-wrapper">
          <table className="modifier-manager-table">
            <thead>
              <tr>
                <th>Name / Code</th>
                <th>Category</th>
                <th>Point value</th>
                <th>Est. cash value</th>
                <th>Currency</th>
                <th>Status</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                editingId === t.award_type_id ? (
                  /* Inline edit row */
                  <tr key={t.award_type_id} style={{ background: 'rgb(var(--color-primary) / 0.04)' }}>
                    <td colSpan={7} style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={S.grid2}>
                          <Field label="Name" required>
                            <input style={S.input} value={editForm.award_type_name}
                              onChange={e => setEditForm(f => ({ ...f, award_type_name: e.target.value }))} />
                          </Field>
                          <Field label="Active">
                            <select style={S.select} value={String(editForm.is_active)}
                              onChange={e => setEditForm(f => ({ ...f, is_active: e.target.value === 'true' }))}>
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </select>
                          </Field>
                          <Field label="Point value" hint="Optional">
                            <input type="number" style={S.input} min="0" step="0.01"
                              value={editForm.point_value}
                              onChange={e => setEditForm(f => ({ ...f, point_value: e.target.value }))} />
                          </Field>
                          <Field label="Est. cash value" hint="Used for FMV if fmv_reporting_required=true">
                            <input type="number" style={S.input} min="0" step="0.01"
                              value={editForm.estimated_cash_value}
                              onChange={e => setEditForm(f => ({ ...f, estimated_cash_value: e.target.value }))} />
                          </Field>
                        </div>
                        <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
                          award_type_code and award_category are immutable after creation.
                        </div>
                        {saveError && (
                          <div className="modifier-manager-error-state" style={{ padding: '8px 12px' }}>
                            <p style={{ margin: 0 }}>{saveError}</p>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" className="primary-button"
                            disabled={saving || !editForm.award_type_name}
                            onClick={() => handleSaveEdit(t.award_type_id)}>
                            {saving ? 'Saving…' : 'Save changes'}
                          </button>
                          <button type="button" className="secondary-button"
                            onClick={() => { setEditingId(null); setSaveError(null); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  /* Normal display row */
                  <tr key={t.award_type_id}>
                    <td>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>{t.award_type_name}</div>
                      <code style={{ fontSize: 11, fontFamily: 'ui-monospace,monospace', background: 'rgb(var(--color-surface-muted))', padding: '1px 5px', borderRadius: 3, border: '1px solid rgb(var(--color-border))' }}>
                        {t.award_type_code}
                      </code>
                    </td>
                    <td style={{ fontSize: 12 }}>{AWARD_CATEGORY_LABELS[t.award_category] ?? t.award_category}</td>
                    <td style={{ fontSize: 12 }}>{t.point_value != null ? _fmt(t.point_value) : '—'}</td>
                    <td style={{ fontSize: 12 }}>{t.estimated_cash_value != null ? _fmt(t.estimated_cash_value) : '—'}</td>
                    <td style={{ fontSize: 12 }}>{t.currency_code ?? '—'}</td>
                    <td>
                      <span className={`modifier-manager-badge ${t.is_active ? 'modifier-manager-badge--complete' : 'modifier-manager-badge--readonly'}`}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="text-button"
                        onClick={() => startEdit(t)}
                        aria-label={`Edit ${t.award_type_name}`}>
                        Edit
                      </button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <CollapsibleSection title="New Award Type" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={S.grid2}>
              <Field label="Award type code" required hint="Immutable after creation — e.g. GIFT_CARD_50">
                <input style={S.input} value={createForm.award_type_code}
                  onChange={e => setCreateForm(f => ({ ...f, award_type_code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. GIFT_CARD_50" />
              </Field>
              <Field label="Award type name" required>
                <input style={S.input} value={createForm.award_type_name}
                  onChange={e => setCreateForm(f => ({ ...f, award_type_name: e.target.value }))}
                  placeholder="e.g. $50 Amazon Gift Card" />
              </Field>
            </div>
            <div style={S.grid3}>
              <Field label="Category" required hint="Immutable after creation">
                <select style={S.select} value={createForm.award_category}
                  onChange={e => setCreateForm(f => ({ ...f, award_category: e.target.value }))}>
                  {AWARD_CATEGORIES.map(c => <option key={c} value={c}>{AWARD_CATEGORY_LABELS[c]}</option>)}
                </select>
              </Field>
              <Field label="Point value" hint="Optional — for points-based programs">
                <input type="number" style={S.input} min="0" step="0.01"
                  value={createForm.point_value}
                  onChange={e => setCreateForm(f => ({ ...f, point_value: e.target.value }))}
                  placeholder="e.g. 500" />
              </Field>
              <Field label="Est. cash value" hint="Used for FMV computation">
                <input type="number" style={S.input} min="0" step="0.01"
                  value={createForm.estimated_cash_value}
                  onChange={e => setCreateForm(f => ({ ...f, estimated_cash_value: e.target.value }))}
                  placeholder="e.g. 50.00" />
              </Field>
            </div>
            <div style={S.grid2}>
              <Field label="Currency code" hint="3-letter ISO code — optional">
                <input style={S.input} value={createForm.currency_code} maxLength={3}
                  onChange={e => setCreateForm(f => ({ ...f, currency_code: e.target.value.toUpperCase() }))}
                  placeholder="USD" />
              </Field>
              <Field label="Active">
                <select style={S.select} value={String(createForm.is_active)}
                  onChange={e => setCreateForm(f => ({ ...f, is_active: e.target.value === 'true' }))}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </Field>
            </div>

            {createError && (
              <div className="modifier-manager-error-state">
                <p style={{ margin: 0 }}>{createError}</p>
              </div>
            )}

            <div className="modifier-manager-footer">
              <span className="modifier-manager-footer-status" />
              <div className="modifier-manager-footer-actions">
                <button type="button" className="secondary-button"
                  onClick={() => { resetCreateForm(); setShowCreate(false); }}>
                  Cancel
                </button>
                <button type="button" className="primary-button"
                  disabled={creating || !createValid} onClick={handleCreate}>
                  {creating ? 'Creating…' : 'Create award type'}
                </button>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 2 — GRANTS
// §67.7 GET /non-cash-award-grants (list)
// §67.4 POST /non-cash-award-grants (create — manager_spot | spiff only)
// §67.5 POST /non-cash-award-grants/{id}/approve (NN-10: granted_by ≠ user)
// §67.6 POST /non-cash-award-grants/{id}/issue (must be approved first)
// ===========================================================================
function GrantsPanel({ fiscalPeriodId, currentUser, onSelectGrantForActivity }) {
  const [grants, setGrants]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');

  // Create grant form
  const [showCreate, setShowCreate] = useState(false);
  const [awardTypes, setAwardTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    award_type_id: '', position_id: '', fiscal_period_id: fiscalPeriodId ?? '',
    grant_source: 'manager_spot', source_reference_id: '', quantity: 1,
    granted_by: '',
  });
  const [creating, setCreating]     = useState(false);
  const [createError, setCreateError] = useState(null);

  // Per-row action state — keyed by grant_id
  const [actionLoading, setActionLoading] = useState({});
  const [actionError, setActionError]     = useState({});

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadGrants = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (fiscalPeriodId) params.set('fiscal_period_id', String(fiscalPeriodId));
      if (filterStatus)   params.set('grant_status', filterStatus);
      if (filterSource)   params.set('grant_source', filterSource);
      const d = await apiFetch(`${API_BASE}/non-cash-award-grants?${params}`);
      if (mountedRef.current) setGrants(d.items ?? []);
    } catch (e) {
      if (mountedRef.current) setError(e.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fiscalPeriodId, filterStatus, filterSource, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadGrants(); }, [loadGrants]);

  // Load award types for create form dropdown
  useEffect(() => {
    if (!showCreate) return;
    setTypesLoading(true);
    apiFetch(`${API_BASE}/non-cash-award-types?is_active=true&limit=200`)
      .then(d => { if (mountedRef.current) setAwardTypes(d.items ?? []); })
      .catch(() => {})
      .finally(() => { if (mountedRef.current) setTypesLoading(false); });
  }, [showCreate]);

  function resetCreateForm() {
    setCreateForm({
      award_type_id: '', position_id: '', fiscal_period_id: fiscalPeriodId ?? '',
      grant_source: 'manager_spot', source_reference_id: '', quantity: 1, granted_by: '',
    });
    setCreateError(null);
  }

  async function handleCreate() {
    setCreating(true); setCreateError(null);
    try {
      const body = {
        award_type_id:       createForm.award_type_id,
        position_id:         createForm.position_id,
        fiscal_period_id:    parseInt(createForm.fiscal_period_id, 10),
        grant_source:        createForm.grant_source,
        source_reference_id: createForm.source_reference_id || null,
        quantity:            parseInt(createForm.quantity, 10) || 1,
        granted_by:          createForm.granted_by,
      };
      await apiFetch(`${API_BASE}/non-cash-award-grants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      resetCreateForm();
      setShowCreate(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleApprove(grantId) {
    setActionLoading(s => ({ ...s, [grantId]: 'approving' }));
    setActionError(s => ({ ...s, [grantId]: null }));
    try {
      await apiFetch(`${API_BASE}/non-cash-award-grants/${grantId}/approve`, { method: 'POST' });
      setRefreshKey(k => k + 1);
    } catch (e) {
      setActionError(s => ({ ...s, [grantId]: handleApprovalError(e) }));
    } finally {
      setActionLoading(s => ({ ...s, [grantId]: null }));
    }
  }

  async function handleIssue(grantId) {
    setActionLoading(s => ({ ...s, [grantId]: 'issuing' }));
    setActionError(s => ({ ...s, [grantId]: null }));
    try {
      await apiFetch(`${API_BASE}/non-cash-award-grants/${grantId}/issue`, { method: 'POST' });
      setRefreshKey(k => k + 1);
    } catch (e) {
      setActionError(s => ({ ...s, [grantId]: e.message }));
    } finally {
      setActionLoading(s => ({ ...s, [grantId]: null }));
    }
  }

  const myUserId = currentUser?.user_id ?? currentUser?.user_account_id;

  const filtered = grants.filter(g =>
    !search ||
    String(g.position_id ?? '').toLowerCase().includes(search.toLowerCase()) ||
    String(g.grant_id ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (g.grant_source ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const pendingCount = grants.filter(g => g.grant_status === 'pending' && ROUTER_GRANT_SOURCES.has(g.grant_source)).length;

  const createValid = createForm.award_type_id && createForm.position_id &&
    createForm.fiscal_period_id && createForm.granted_by;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Summary strip */}
      {pendingCount > 0 && (
        <div style={{ padding: '8px 14px', borderRadius: 8, background: 'rgb(var(--color-warning) / 0.08)', border: '1px solid rgb(var(--color-warning) / 0.25)', fontSize: 12, color: 'rgb(var(--color-text))' }}>
          <strong>{pendingCount}</strong> grant{pendingCount !== 1 ? 's' : ''} awaiting approval.
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <input type="text" placeholder="Search grants…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="issued">Issued</option>
        </select>
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }}>
          <option value="">All sources</option>
          <option value="manager_spot">Manager Spot</option>
          <option value="spiff">SPIFF</option>
          <option value="contest">Contest (engine)</option>
          <option value="bonus_pool">Bonus Pool (engine)</option>
        </select>
        <button type="button" onClick={() => setRefreshKey(k => k + 1)}
          style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', background: 'none', border: 'none', cursor: 'pointer' }}>
          ↻ Refresh
        </button>
        <button type="button" className="primary-button" style={{ marginLeft: 'auto' }}
          onClick={() => { resetCreateForm(); setShowCreate(true); }}>
          + New Grant
        </button>
      </div>

      {/* Row count */}
      <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
        {loading ? 'Loading…' : `${filtered.length} of ${grants.length} grant${grants.length !== 1 ? 's' : ''}`}
      </div>

      {error && <div style={{ padding: '12px 16px', color: 'rgb(var(--color-danger))', fontSize: 13 }} role="alert">{error}</div>}

      {/* Grants table — D-1 */}
      {!loading && filtered.length === 0 && !showCreate && (
        <div className="modifier-manager-empty-state">
          <h4>No grants found</h4>
          <p>{filterStatus || filterSource || search ? 'No grants match the current filters.' : 'No grants have been created yet. Create the first grant above.'}</p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="modifier-manager-table-wrapper">
          <table className="modifier-manager-table">
            <thead>
              <tr>
                <th>Grant ID</th>
                <th>Position</th>
                <th>Source</th>
                <th>Qty</th>
                <th>FMV</th>
                <th>Status</th>
                <th>Granted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => {
                const isRouterSource  = ROUTER_GRANT_SOURCES.has(g.grant_source);
                const isEngineSource  = ENGINE_GRANT_SOURCES.has(g.grant_source);
                const isSelf          = myUserId && g.granted_by && String(myUserId) === String(g.granted_by);
                const canApprove      = isRouterSource && g.grant_status === 'pending' && !isSelf;
                const selfBlocked     = isRouterSource && g.grant_status === 'pending' && isSelf;
                const canIssue        = isRouterSource && g.grant_status === 'approved';
                const rowLoading      = actionLoading[g.grant_id];
                const rowError        = actionError[g.grant_id];

                return (
                  <tr key={g.grant_id} style={{ cursor: 'pointer' }}
                    onClick={() => onSelectGrantForActivity?.(g.grant_id)}>
                    <td style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11 }}>
                      {String(g.grant_id).slice(0, 8)}…
                    </td>
                    <td style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11 }}>
                      {String(g.position_id ?? '').slice(0, 8)}…
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {GRANT_SOURCE_LABELS[g.grant_source] ?? g.grant_source}
                      {isEngineSource && (
                        <span style={{ marginLeft: 4, fontSize: 10, color: 'rgb(var(--color-text-muted))' }}>read-only</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>{g.quantity ?? 1}</td>
                    <td style={{ fontSize: 12 }}>
                      {g.estimated_fmv != null ? _fmt(g.estimated_fmv) : '—'}
                    </td>
                    <td>
                      <span className={`modifier-manager-badge ${GRANT_STATUS_BADGE[g.grant_status] ?? ''}`}>
                        {GRANT_STATUS_LABEL[g.grant_status] ?? g.grant_status}
                      </span>
                    </td>
                    <td style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
                      {g.granted_at ? new Date(g.granted_at).toLocaleDateString() : _iso(g.created_at)}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {canApprove && (
                          <button type="button" className="text-button"
                            disabled={!!rowLoading}
                            onClick={() => handleApprove(g.grant_id)}>
                            {rowLoading === 'approving' ? 'Approving…' : 'Approve'}
                          </button>
                        )}
                        {selfBlocked && (
                          <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
                            NN-10: you granted this
                          </span>
                        )}
                        {canIssue && (
                          <button type="button" className="text-button"
                            disabled={!!rowLoading}
                            onClick={() => handleIssue(g.grant_id)}>
                            {rowLoading === 'issuing' ? 'Issuing…' : 'Issue'}
                          </button>
                        )}
                        {isEngineSource && <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>—</span>}
                        {rowError && (
                          <span style={{ fontSize: 11, color: 'rgb(var(--color-danger))' }} role="alert">
                            {rowError}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create grant form */}
      {showCreate && (
        <CollapsibleSection title="New Grant" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
              Only <strong>manager_spot</strong> and <strong>spiff</strong> grants can be created via the API.
              Contest and bonus pool grants are engine-written (dual-writer rule).
            </div>

            <div style={S.grid2}>
              <Field label="Award type" required>
                <select style={S.select} value={createForm.award_type_id}
                  onChange={e => setCreateForm(f => ({ ...f, award_type_id: e.target.value }))}>
                  <option value="">— Select award type —</option>
                  {typesLoading && <option disabled>Loading types…</option>}
                  {awardTypes.map(t => (
                    <option key={t.award_type_id} value={t.award_type_id}>
                      {t.award_type_name} ({AWARD_CATEGORY_LABELS[t.award_category] ?? t.award_category})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Grant source" required>
                <select style={S.select} value={createForm.grant_source}
                  onChange={e => setCreateForm(f => ({ ...f, grant_source: e.target.value }))}>
                  <option value="manager_spot">Manager Spot</option>
                  <option value="spiff">SPIFF</option>
                </select>
              </Field>
            </div>

            <div style={S.grid2}>
              <Field label="Position ID" required hint="UUID of recipient position">
                <input style={S.input} value={createForm.position_id}
                  onChange={e => setCreateForm(f => ({ ...f, position_id: e.target.value }))}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </Field>
              <Field label="Fiscal period ID" required>
                <input type="number" style={S.input} value={createForm.fiscal_period_id}
                  onChange={e => setCreateForm(f => ({ ...f, fiscal_period_id: e.target.value }))} />
              </Field>
            </div>

            <div style={S.grid2}>
              <Field label="Quantity" hint="Default 1">
                <input type="number" style={S.input} min="1" value={createForm.quantity}
                  onChange={e => setCreateForm(f => ({ ...f, quantity: e.target.value }))} />
              </Field>
              <Field label="Source reference ID" hint="Optional — link to SPIFF plan ID or other reference">
                <input style={S.input} value={createForm.source_reference_id}
                  onChange={e => setCreateForm(f => ({ ...f, source_reference_id: e.target.value }))} />
              </Field>
            </div>

            <Field label="Granted by (user ID)" required
              hint="UUID of the granting user. NN-10: this person cannot approve the grant.">
              <input style={S.input} value={createForm.granted_by}
                onChange={e => setCreateForm(f => ({ ...f, granted_by: e.target.value }))}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </Field>

            {createError && (
              <div className="modifier-manager-error-state">
                <p style={{ margin: 0 }}>{createError}</p>
              </div>
            )}

            <div className="modifier-manager-footer">
              <span className="modifier-manager-footer-status" />
              <div className="modifier-manager-footer-actions">
                <button type="button" className="secondary-button"
                  onClick={() => { resetCreateForm(); setShowCreate(false); }}>
                  Cancel
                </button>
                <button type="button" className="primary-button"
                  disabled={creating || !createValid} onClick={handleCreate}>
                  {creating ? 'Creating…' : 'Create grant'}
                </button>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 3 — ACTIVITY
// §30.1 GET /audit-logs entity_type=non_cash_award_grant [entity_id=<uuid>]
// When a grant is selected (from row click in Grants tab), filters by entity_id.
// Falls back to full non_cash_award_grant activity when no grant selected.
// Response shape: { data: [...], pagination: { next_cursor, has_more, limit } }
// Cursor pagination via ISO timestamp cursor param.
// ===========================================================================
function ActivityPanel({ selectedGrantId }) {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [hasMore, setHasMore]   = useState(false);
  const [cursor, setCursor]     = useState(null);
  const [search, setSearch]     = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const PAGE_SIZE = 25;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Reset cursor when grant selection or refresh changes
  useEffect(() => {
    setEntries([]);
    setCursor(null);
  }, [selectedGrantId, refreshKey]);

  useEffect(() => {
    setLoading(true);
    // §30.1: entity_type filter always set; entity_id uuid filter when grant selected
    const params = new URLSearchParams({
      entity_type: 'non_cash_award_grant',
      limit: String(PAGE_SIZE),
    });
    if (selectedGrantId) params.set('entity_id', selectedGrantId);
    if (cursor) params.set('cursor', cursor);

    apiFetch(`${API_BASE}/audit-logs?${params}`)
      .then(d => {
        if (!mountedRef.current) return;
        const newEntries = d.data ?? [];
        setEntries(prev => cursor ? [...prev, ...newEntries] : newEntries);
        setHasMore(d.pagination?.has_more ?? false);
      })
      .catch(() => { if (mountedRef.current) setEntries([]); })
      .finally(() => { if (mountedRef.current) setLoading(false); });
  }, [selectedGrantId, cursor, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadMore() {
    if (entries.length > 0) setCursor(entries[entries.length - 1].created_at);
  }

  function refresh() {
    setEntries([]);
    setCursor(null);
    setRefreshKey(k => k + 1);
  }

  // Event label map — sourced from p4_non_cash_router.py _audit() call sites
  function eventLabel(eventType) {
    const map = {
      'p4.non_cash_award_type.created':  'Award type created',
      'p4.non_cash_award_type.updated':  'Award type updated',
      'p4.non_cash_award_grant.created': 'Grant created (Pending)',
      'p4.non_cash_award_grant.approved':'Grant approved',
      'p4.non_cash_award_grant.issued':  'Grant issued',
    };
    return map[eventType] ?? eventType;
  }

  const filtered = search
    ? entries.filter(e =>
        (e.event_type ?? '').toLowerCase().includes(search.toLowerCase()) ||
        eventLabel(e.event_type).toLowerCase().includes(search.toLowerCase()) ||
        (e.change_summary ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : entries;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'rgb(var(--color-text))' }}>Activity</span>
          {selectedGrantId && (
            <span style={{ marginLeft: 8, fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
              Grant {String(selectedGrantId).slice(0, 8)}… · click a grant row in the Grants tab to filter
            </span>
          )}
          {!selectedGrantId && (
            <span style={{ marginLeft: 8, fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
              All non-cash grant activity · click a grant row to filter by grant
            </span>
          )}
        </div>
        <button type="button" className="icon-button" onClick={refresh} title="Refresh" aria-label="Refresh activity">↻</button>
      </div>

      <input
        style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
        placeholder="Filter activity…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {loading && entries.length === 0 && (
        <div className="modifier-manager-loading">Loading activity…</div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="modifier-manager-empty-state">
          <h4>No activity yet</h4>
          <p>Actions taken on non-cash grants will appear here.</p>
        </div>
      )}

      {filtered.map((e, i) => (
        <div key={e.audit_log_id ?? i}
          style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid rgb(var(--color-border))' }}>
          <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', minWidth: 140, flexShrink: 0 }}>
            {_iso(e.created_at)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            <span style={{ fontSize: 13, color: 'rgb(var(--color-text))' }}>{eventLabel(e.event_type)}</span>
            {e.change_summary && (
              <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{e.change_summary}</span>
            )}
            {e.user_id && (
              <span style={{ fontSize: 10, color: 'rgb(var(--color-text-muted))', fontFamily: 'ui-monospace,monospace' }}>
                by {String(e.user_id).slice(0, 8)}…
              </span>
            )}
          </div>
        </div>
      ))}

      {hasMore && (
        <div style={{ paddingTop: 8 }}>
          <button type="button" className="secondary-button" disabled={loading} onClick={loadMore}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// MAIN — NonCashWorkspace (C-69)
// ===========================================================================
export default function NonCashWorkspace({ fiscalPeriodId, currentUser, onClose, onSaved }) {
  const [activeTab, setActiveTab]           = useState('award_types');
  // Selected grant for activity drill-down (set by clicking a row in Grants tab)
  const [selectedGrantId, setSelectedGrantId] = useState(null);

  // When a grant row is clicked → switch to Activity tab filtered by that grant
  function handleSelectGrantForActivity(grantId) {
    setSelectedGrantId(grantId);
    setActiveTab('activity');
  }

  // Pending grants attention indicator
  // Not computed here — GrantsPanel owns its data. Tab indicator uses a
  // ref count surfaced via callback when grants load.
  const [pendingGrantCount, setPendingGrantCount] = useState(0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 0 12px', borderBottom: '1px solid rgb(var(--color-border))', marginBottom: 0 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgb(var(--color-text-muted))', margin: '0 0 4px' }}>
            EXECUTE · Programs &amp; Adjustments · Non-Cash Awards
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'rgb(var(--color-text))', margin: 0 }}>
              Non-Cash Awards
            </h1>
            <span className="modifier-manager-badge modifier-manager-badge--complete">Standard</span>
          </div>
          <p style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', margin: '4px 0 0' }}>
            Manage award type catalog and per-recipient grants.
            {fiscalPeriodId && <> · Period: <strong>{fiscalPeriodId}</strong></>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="secondary-button" onClick={onClose}>← Back</button>
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgb(var(--color-border))', marginBottom: 20 }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 18px', fontSize: 13,
              fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? 'rgb(var(--color-primary))' : 'rgb(var(--color-text-muted))',
              background: 'none', border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid rgb(var(--color-primary))' : '2px solid transparent',
              cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s',
              marginBottom: -1,
            }}
          >
            {tab.label}
            {/* Pending attention indicator on Grants tab */}
            {tab.key === 'grants' && pendingGrantCount > 0 && (
              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 10, background: 'rgb(var(--color-warning) / 0.15)', color: 'rgb(var(--color-warning))' }}>
                {pendingGrantCount}
              </span>
            )}
            {/* Activity tab indicator when a grant is selected */}
            {tab.key === 'activity' && selectedGrantId && (
              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 10, background: 'rgb(var(--color-primary) / 0.12)', color: 'rgb(var(--color-primary))' }}>
                filtered
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab content ────────────────────────────────────────────────── */}
      <div style={{ minHeight: 400 }}>

        {activeTab === 'award_types' && (
          <AwardTypesPanel />
        )}

        {activeTab === 'grants' && (
          <GrantsPanel
            fiscalPeriodId={fiscalPeriodId}
            currentUser={currentUser}
            onSelectGrantForActivity={handleSelectGrantForActivity}
          />
        )}

        {activeTab === 'activity' && (
          <ActivityPanel
            selectedGrantId={selectedGrantId}
          />
        )}

      </div>
    </div>
  );
}
